import type { Fraction } from '../util/fractionType';
import type { SegmentInfo, SegmentProvider } from '../types/segmentProvider';
import { SharedSegmentProvider } from './sharedSegmentProvider';
import type { VirtualClipInfo } from '../dto/playableItemDto';
import type { PlaybackQuality, QualityOption } from '../types/playbackQuality';
import { invariant } from '../util/invariant';
import type { EncodedData } from '../mpegts/demuxerTypes';
import { Mutex } from '../async/mutex';
import type { Logger } from '../types/logger';
import { exceptionToString } from '../util/exception';

export class CachedSegmentProvider implements SegmentProvider {
  private cleanUp: (() => void)[] = [];

  constructor(
    private logger: Logger,
    private cacheEntry: CacheEntry,
    private ignoreVideo: boolean,
    private ignoreAudio: boolean,
  ) { }

  public static async create(
    logger: Logger,
    key: CacheKey,
    factory: () => Promise<SegmentProvider>,
    ignoreVideo: boolean,
    ignoreAudio: boolean,
  ) {
    const cacheEntry = getCacheEntry(logger, key);
    try {
      await cacheEntry.mutex.withLock(async () => {
        if (cacheEntry.segmentProvider === null) {
          logger.debug(`CachedSegmentProvider: creating segment provider ${key}`);
          cacheEntry.segmentProvider = new SharedSegmentProvider(await factory());
        }
      });
    } catch (error) {
      logger.warn(`CachedSegmentProvider: Failed to create segment provider ${key}: ${exceptionToString(error)}`);
      cacheEntry.unref(logger);
      throw error;
    }
    return new CachedSegmentProvider(logger, cacheEntry, ignoreVideo, ignoreAudio);
  }

  private segmentProvider(): SegmentProvider {
    invariant(this.cacheEntry.segmentProvider, 'Use of closed segment provider');
    return this.cacheEntry.segmentProvider;
  }

  async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
    return await this.segmentProvider().getSegmentInfoAt(position);
  }

  async getSegment(id: string): Promise<EncodedData> {
    const encodedData = await this.segmentProvider().getSegment(id);
    return {
      encodedVideoData: this.ignoreVideo ? undefined : encodedData.encodedVideoData,
      encodedAudioData: this.ignoreAudio ? [] : encodedData.encodedAudioData,
    };
  }

  async getDuration(): Promise<Fraction> {
    return await this.segmentProvider().getDuration();
  }

  async getVirtualClipInfo(): Promise<VirtualClipInfo | null> {
    return await this.segmentProvider().getVirtualClipInfo();
  }

  async getCurrentQuality(): Promise<PlaybackQuality> {
    return await this.segmentProvider().getCurrentQuality();
  }

  async getAvailableQualities(): Promise<QualityOption[]> {
    return await this.segmentProvider().getAvailableQualities();
  }

  addDurationChangedListener(onDurationChanged: (duration: Fraction) => void): () => void {
    const removeListener = this.segmentProvider().addDurationChangedListener(onDurationChanged);
    this.cleanUp.push(removeListener);
    return () => {
      removeListener();
      // If this is explicitly removed from the outside we don't need to clean
      // this up automatically when closing the segment provider
      this.cleanUp = this.cleanUp.filter((r) => removeListener !== r);
    };
  }

  close(): void {
    this.cleanUp.forEach((cleanUp) => cleanUp());

    // In order to avoid destroying segment providers when they are are quickly
    // recreated we defer the unref by 100ms. One place this happens in
    // practice is when dragging an item to the cutter timeline. There is a
    // temporarily added timeline entry while dragging which is removed and a
    // permanent entry added when the user completes the drag operation.
    setTimeout(() => this.cacheEntry.unref(this.logger), 1000);
  }
}


type CacheKey = string;

interface CacheEntry {
  mutex: Mutex,
  segmentProvider: SegmentProvider | null,
  referenceCount: number,
  unref: (logger: Logger) => void,
}


const globalCache = new Map<CacheKey, CacheEntry>();

function getCacheEntry(logger: Logger, key: string): CacheEntry {
  let cacheEntry = globalCache.get(key);
  if (cacheEntry === undefined) {
    logger.debug(`CachedSegmentProvider: Creating new cache entry for: ${key}`);
    cacheEntry = {
      mutex: new Mutex(),
      segmentProvider: null,
      referenceCount: 0,
      unref(logger: Logger) {
        this.referenceCount -= 1;
        logger.debug(`CachedSegmentProvider: Decrementing ref count for: ${key} => ${this.referenceCount}`);
        invariant(this.referenceCount >= 0, 'Negative ref count on CacheSegmentProvider');
        if (this.referenceCount === 0) {
          logger.debug(`CachedSegmentProvider: Destroying cache entry for: ${key}`);
          this.segmentProvider?.close();
          this.segmentProvider = null;
          globalCache.delete(key);
        }
      },
    };
    globalCache.set(key, cacheEntry);
  }
  cacheEntry.referenceCount += 1;
  logger.debug(`CachedSegmentProvider: Increasing ref count for: ${key} => ${cacheEntry.referenceCount}`);
  return cacheEntry;
}

